import { useEffect, useRef, useState, useCallback } from 'react'
import { logEvent } from 'firebase/analytics'
import * as Sentry from '@sentry/react'
import Peer from 'peerjs'
import { analytics } from '../firebase'

async function logConnectionType(pc, role) {
  try {
    const stats = await pc.getStats()
    let selectedPairId = null

    stats.forEach((report) => {
      if (report.type === 'transport' && report.selectedCandidatePairId) {
        selectedPairId = report.selectedCandidatePairId
      }
    })

    if (!selectedPairId) return

    const pair = stats.get(selectedPairId)
    if (!pair) return

    const local = stats.get(pair.localCandidateId)
    const remote = stats.get(pair.remoteCandidateId)

    logEvent(analytics, 'webrtc_connection', {
      role,
      local_candidate: local?.candidateType,
      remote_candidate: remote?.candidateType
    })
  } catch (err) {
    Sentry.captureException(err)
  }
}

async function logIceFailure(pc, role) {
  try {
    const stats = await pc.getStats()
    const candidates = { local: [], remote: [] }
    const pairs = []

    stats.forEach((report) => {
      if (report.type === 'local-candidate') {
        candidates.local.push({ type: report.candidateType, protocol: report.protocol, address: report.address })
      }
      if (report.type === 'remote-candidate') {
        candidates.remote.push({ type: report.candidateType, protocol: report.protocol, address: report.address })
      }
      if (report.type === 'candidate-pair') {
        pairs.push({ state: report.state, localId: report.localCandidateId, remoteId: report.remoteCandidateId })
      }
    })

    Sentry.captureMessage(`ICE failed (${role})`, {
      level: 'error',
      extra: {
        localCandidates: candidates.local,
        remoteCandidates: candidates.remote,
        candidatePairs: pairs,
        iceGatheringState: pc.iceGatheringState,
        signalingState: pc.signalingState
      }
    })
  } catch {
    Sentry.captureMessage(`ICE failed (${role})`, { level: 'error' })
  }
}

async function fetchIceServers() {
  try {
    //const resp = await fetch(`https://callmom.metered.live/api/v1/turn/credentials?apiKey=${import.meta.env.VITE_METERED_API_KEY}`)
    const resp = await fetch('/api/v1/turn/credentials')
    const servers = await resp.json()
    return servers
  } catch (err) {
    Sentry.captureException(err)
    return [{ urls: 'stun:stun.l.google.com:19302' }]
  }
}

// Returns 0-4 based on round-trip time and packet loss
function computeQuality(rtt, packetLoss) {
  if (rtt === null) return 4
  if (packetLoss > 10 || rtt > 400) return 1
  if (packetLoss > 5 || rtt > 250) return 2
  if (packetLoss > 2 || rtt > 150) return 3
  return 4
}

export function usePeer(roomId, localStream, userInfo) {
  const [remoteStream, setRemoteStream] = useState(null)
  const [connectionStatus, setConnectionStatus] = useState('initializing')
  const [remoteUser, setRemoteUser] = useState(null)
  const [callQuality, setCallQuality] = useState(4)
  const peerRef = useRef(null)
  const callRef = useRef(null)
  const dataConnRef = useRef(null)
  const endingRef = useRef(false)
  const remoteEndedRef = useRef(false)
  const qualityIntervalRef = useRef(null)
  const prevBytesRef = useRef({ received: 0, timestamp: 0 })

  const stopQualityMonitor = useCallback(() => {
    if (qualityIntervalRef.current) {
      clearInterval(qualityIntervalRef.current)
      qualityIntervalRef.current = null
    }
  }, [])

  const startQualityMonitor = useCallback((pc) => {
    stopQualityMonitor()
    prevBytesRef.current = { received: 0, timestamp: 0 }
    qualityIntervalRef.current = setInterval(async () => {
      try {
        const stats = await pc.getStats()
        let rtt = null
        let packetsLost = 0
        let packetsReceived = 0

        stats.forEach((report) => {
          if (report.type === 'candidate-pair' && report.state === 'succeeded') {
            rtt = report.currentRoundTripTime * 1000
          }
          if (report.type === 'inbound-rtp' && report.kind === 'video') {
            packetsLost = report.packetsLost || 0
            packetsReceived = report.packetsReceived || 0
          }
        })

        const total = packetsReceived + packetsLost
        const packetLoss = total > 0 ? (packetsLost / total) * 100 : 0
        setCallQuality(computeQuality(rtt, packetLoss))
      } catch {
        // pc might be closed
      }
    }, 3000)
  }, [stopQualityMonitor])

  const destroy = useCallback(() => {
    stopQualityMonitor()
    dataConnRef.current = null
    if (callRef.current) {
      callRef.current.close()
      callRef.current = null
    }
    if (peerRef.current) {
      peerRef.current.destroy()
      peerRef.current = null
    }
    setRemoteStream(null)
    setRemoteUser(null)
    setCallQuality(4)
  }, [stopQualityMonitor])

  const cleanup = useCallback((sendEnded = false) => {
    if (sendEnded && dataConnRef.current?.open) {
      dataConnRef.current.send({ type: 'call-ended' })
      // Delay destroy so the message has time to arrive
      setTimeout(destroy, 300)
    } else {
      destroy()
    }
  }, [destroy])

  const setupIceListeners = useCallback((pc, onConnected) => {
    pc.addEventListener('iceconnectionstatechange', () => {
      const state = pc.iceConnectionState
      if (state) Sentry.addBreadcrumb({ category: 'webrtc', message: `ICE state: ${state}`, level: 'info' })
      if (state === 'connected') {
        logConnectionType(pc)
        onConnected?.()
      }
      if (state === 'failed') {
        logIceFailure(pc)
        setConnectionStatus('error')
      }
    })
  }, [])

  const setupDataConn = useCallback((conn, onCallEnded) => {
    conn.on('open', () => {
      conn.send({ type: 'user-info', displayName: userInfo?.displayName, photoURL: userInfo?.photoURL })
    })
    conn.on('data', (data) => {
      if (data.type === 'user-info') {
        setRemoteUser({ displayName: data.displayName, photoURL: data.photoURL })
      }
      if (data.type === 'call-ended') {
        onCallEnded()
      }
    })
    conn.on('close', () => {
      Sentry.addBreadcrumb({ category: 'datachannel', message: `data conn closed`, level: 'info' })
    })
    conn.on('error', (err) => {
      Sentry.captureException(err, { tags: { context: 'datachannel' } })
    })
  }, [userInfo])

  const setupCallListeners = useCallback((call, onClose) => {
    call.on('stream', (stream) => {
      setRemoteStream(stream)
      setConnectionStatus('connected')
      if (call.peerConnection) startQualityMonitor(call.peerConnection)
    })
    call.on('close', () => {
      stopQualityMonitor()
      setRemoteStream(null)
      setRemoteUser(null)
      callRef.current = null
      onClose()
    })
    call.on('error', (err) => {
      Sentry.captureException(err, { tags: { context: 'call' } })
      setConnectionStatus('error')
    })
  }, [startQualityMonitor, stopQualityMonitor])

  useEffect(() => {
    if (!localStream || !roomId) return

    let cancelled = false

    async function init() {
      const iceServers = await fetchIceServers()
      if (cancelled) return

      const peerConfig = {
        host: 'api.callmom.io',
        path: '/peer',
        secure: true,
        config: { iceServers },
        debug: 1
      }

      // Try to create peer with room ID (this makes us the host)
      const peer = new Peer(roomId, peerConfig)
      peerRef.current = peer

      peer.on('open', (id) => {
        if (id === roomId) {
          setConnectionStatus('waiting')
        }
      })

      peer.on('error', (err) => {
        if (err.type === 'unavailable-id') {
          peer.destroy()
          joinRoom()
        } else {
          Sentry.captureException(err)
          setConnectionStatus('error')
        }
      })

      remoteEndedRef.current = false

      peer.on('connection', (conn) => {
        dataConnRef.current = conn
        setupDataConn(conn, () => { remoteEndedRef.current = true })
      })

      peer.on('call', (call) => {
        const pc = callRef.current?.peerConnection
        if (pc && ['connected', 'completed'].includes(pc.iceConnectionState)) {
          call.close()
          return
        }
        // Close stale call if it exists
        if (callRef.current) {
          callRef.current.close()
          callRef.current = null
        }
        setConnectionStatus('connecting')
        call.answer(localStream)
        callRef.current = call

        if (call.peerConnection) setupIceListeners(call.peerConnection)

        setupCallListeners(call, () => {
          if (endingRef.current) return
          if (remoteEndedRef.current) {
            destroy()
            setConnectionStatus('ended')
          } else {
            setConnectionStatus('waiting')
          }
        })
      })

      function joinRoom() {
        const joinerPeer = new Peer(peerConfig)
        peerRef.current = joinerPeer

        joinerPeer.on('open', () => {
          setConnectionStatus('connecting')

          const conn = joinerPeer.connect(roomId)
          dataConnRef.current = conn
          remoteEndedRef.current = false
          setupDataConn(conn, () => { remoteEndedRef.current = true })

          const call = joinerPeer.call(roomId, localStream)
          callRef.current = call
          let wasConnected = false

          if (call.peerConnection) setupIceListeners(call.peerConnection, () => { wasConnected = true })

          setupCallListeners(call, () => {
            if (endingRef.current || remoteEndedRef.current || !wasConnected) {
              setConnectionStatus('ended')
              joinerPeer.destroy()
            } else {
              setConnectionStatus('reconnecting')
              // Host refreshed — retry connecting after a short delay
              setTimeout(() => {
                if (!cancelled) {
                  joinerPeer.destroy()
                  joinRoom()
                }
              }, 2000)
            }
          })
        })

        joinerPeer.on('error', (err) => {
          Sentry.captureException(err)
          if (err.type === 'peer-unavailable') {
            // Host not back yet — retry
            setConnectionStatus('reconnecting')
            setTimeout(() => {
              if (!cancelled) {
                joinerPeer.destroy()
                joinRoom()
              }
            }, 2000)
          } else {
            setConnectionStatus('error')
          }
        })
      }
    }

    init()

    return () => {
      cancelled = true
      cleanup()
    }
  }, [roomId, localStream, cleanup, setupCallListeners, setupDataConn, setupIceListeners, destroy])

  const endCall = useCallback(() => {
    endingRef.current = true
    cleanup(true)
    setConnectionStatus('ended')
  }, [cleanup])

  return { remoteStream, connectionStatus, endCall, remoteUser, callQuality, callRef }
}
